import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { agentApi, workflowApi } from '../services/api';
import { useUIStore } from '../store/index.js';
import { useMultiStepAgentStream } from './useMultiStepAgentStream.js';
import { useWorkflowForm } from './useWorkflowForm.js';
import type { SaveWarning } from './useWorkflowForm.js';
import { useFieldChangeTracker } from './useFieldChangeTracker.js';
import type { WorkflowBuilderStep, ConfigTab } from '../pages/admin/WorkflowBuilderTypes.js';
import { computeStepLabels } from '../pages/admin/WorkflowBuilderTypes.js';
import { useStepResultsFromSnapshots } from './useSnapshots.js';
import { computeContractHash as computeContractHashFn } from '../utils/contract-utils.js';
import { buildWorkflowAssistantImportMessage } from '../lib/workflow-assistant-import.js';
import { useTask } from './useTasks.js';
import { useGraphSave } from './useGraphSave.js';
import { useWorkflowFileDrop } from './useWorkflowFileDrop.js';
import type { AgentTemplateSummary, WorkflowContract, InputContractMap } from '../generated/api/types.gen.js';
import type { PreviewPromptRole } from '../pages/admin/promptPreview.types.js';
import { useToast } from '../components/ui/index.js';

function hasContractProperties(contract: unknown): boolean {
  if (!contract || typeof contract !== 'object' || Array.isArray(contract)) return false;
  const schema =
    (contract as { jsonSchema?: unknown; schema?: unknown }).jsonSchema ??
    (contract as { jsonSchema?: unknown; schema?: unknown }).schema;
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return false;
  const properties = (schema as { properties?: unknown }).properties;
  return Boolean(
    properties && typeof properties === 'object' && !Array.isArray(properties) && Object.keys(properties).length > 0,
  );
}

export function useWorkflowBuilderState(idOverride?: string) {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = idOverride ?? params.id;
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const sourceTaskId = isNew ? (searchParams.get('taskId') ?? undefined) : undefined;

  // Centralized form state with localStorage persistence
  const wf = useWorkflowForm(id);
  const { data: sourceTask } = useTask(sourceTaskId);

  // Per-field change tracking
  const changes = useFieldChangeTracker(wf.form, wf.savedState);

  // UI state (not part of form)
  const [roles, setRoles] = useState<AgentTemplateSummary[]>([]);
  const { notify } = useToast();
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ConfigTab>('config');
  const [pendingChangesOpen, setPendingChangesOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState<string | null>(null);
  const sourceTaskAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isNew || !sourceTaskId || !sourceTask || sourceTaskAppliedRef.current === sourceTaskId) return;
    sourceTaskAppliedRef.current = sourceTaskId;
    wf.setForm({
      ...wf.form,
      name: wf.form.name || `Workflow for ${sourceTask.title}`,
      description: wf.form.description || `Orchestrates task "${sourceTask.title}".\n\nTask goal:\n${sourceTask.goal}`,
      category: wf.form.category || 'task',
      tags: wf.form.tags || 'task,workflow',
    });
    setAssistantMessage(
      [
        'Draft a workflow from this task. Use the task title, goal, project/team labels, and contracts as source context.',
        'Propose user-reviewable workflow steps. Keep Tasks as accountability boundaries and use Workflow steps for orchestration, branching, retry, and contract negotiation.',
        '',
        JSON.stringify(
          {
            action: 'draft-workflow-from-task',
            task: {
              id: sourceTask.id,
              title: sourceTask.title,
              goal: sourceTask.goal,
              project: sourceTask.relations?.project?.name ?? null,
              team: sourceTask.relations?.team?.name ?? null,
              template: sourceTask.relations?.template?.name ?? null,
              inputContract: sourceTask.inputContract,
              outputContract: sourceTask.outputContract,
              executionContract: sourceTask.executionContract,
              resultContract: sourceTask.resultContract,
            },
          },
          null,
          2,
        ),
      ].join('\n'),
    );
  }, [isNew, sourceTaskId, sourceTask, wf]);

  // Layout mode (persisted in UI store)
  const layoutMode = useUIStore((s) => s.workflowBuilderLayout);
  const setLayoutMode = useUIStore((s) => s.setWorkflowBuilderLayout);

  // Prompt preview state
  const [promptPreview, setPromptPreview] = useState<{
    systemPrompt: string;
    userPrompt: string;
    estimatedTokens: number;
    role: PreviewPromptRole;
  } | null>(null);
  const [promptLoading, setPromptLoading] = useState(false);

  // Multi-step agent stream (per-step sessions)
  const multiStream = useMultiStepAgentStream();

  // Step execution results (persisted across steps for chaining)
  const [stepResults, setStepResults] = useState<Record<string, unknown>>({});

  const workflowId = !isNew && id ? Number(id) : undefined;

  // Collapsible sections
  const [focusMode, setFocusMode] = useState(false);
  // An existing workflow opens on its flow and steps; its settings start folded (a new one starts on them).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set(isNew ? [] : ['metadata']));
  const [instructionMode, setInstructionMode] = useState<'freetext' | 'template'>('freetext');

  // Load templates on mount (legacy name: "roles")
  useEffect(() => {
    agentApi
      .listTemplates()
      .then((d) => setRoles(d.data))
      .catch(() => {});
  }, []);

  // Load debug snapshots via React Query and build stepResults
  const snapshotTemplateId = !isNew && id ? Number(id) : undefined;
  const { stepResults: snapshotStepResults } = useStepResultsFromSnapshots(snapshotTemplateId);

  // Merge snapshot results into stepResults state (snapshots provide the base, local debug runs override)
  useEffect(() => {
    if (Object.keys(snapshotStepResults).length > 0) {
      setStepResults((prev) => {
        const merged = { ...prev };
        for (const [stepId, snap] of Object.entries(snapshotStepResults)) {
          if (merged[stepId] === undefined) {
            merged[stepId] = snap.output;
          }
        }
        return merged;
      });
    }
  }, [snapshotStepResults]);

  // Compute debug statuses (has_output vs drifted) for each step
  const [debugStatuses, setDebugStatuses] = useState<Record<string, 'has_output' | 'drifted'>>({});
  useEffect(() => {
    let cancelled = false;
    const computeStatuses = async () => {
      const statuses: Record<string, 'has_output' | 'drifted'> = {};
      for (const step of wf.form.steps) {
        const stepKey = step.id.toString();
        const snap = snapshotStepResults[stepKey];
        if (!snap) continue;
        if (snap.contractHash && step.outputContract) {
          const contractForHash = {
            outputContract: step.outputContract,
            negotiationStatus: step.contractNegotiationStatus ?? 'pending',
            version: step.contractVersion ?? 0,
          };
          const currentHash = await computeContractHashFn(contractForHash);
          statuses[stepKey] = currentHash && currentHash !== snap.contractHash ? 'drifted' : 'has_output';
        } else {
          statuses[stepKey] = 'has_output';
        }
      }
      if (!cancelled) setDebugStatuses(statuses);
    };
    computeStatuses();
    return () => {
      cancelled = true;
    };
  }, [snapshotStepResults, wf.form.steps]);

  // Sync step selection to multi-stream
  useEffect(() => {
    multiStream.selectStep(selectedStep);
  }, [selectedStep]);

  // Load prompt preview when tab changes or step changes
  const sel = wf.form.steps.find((s) => s.id.toString() === selectedStep);
  const selectedRole = sel?.templateSlug;
  useEffect(() => {
    if ((activeTab === 'prompt' || activeTab === 'debug') && selectedStep) {
      loadPromptPreview();
    }
  }, [activeTab, selectedStep, selectedRole]);

  // Sync instruction mode when step selection changes
  useEffect(() => {
    if (sel?.promptMode === 'user') {
      setInstructionMode('template');
    } else if ((sel?.additionalInstructions ?? sel?.userPromptTemplate)?.includes('{{')) {
      setInstructionMode('template');
    } else {
      setInstructionMode('freetext');
    }
  }, [selectedStep]);

  // Step labels: parallel steps get 3.1, 3.2 etc.
  const stepLabels = useMemo(() => computeStepLabels(wf.form.steps), [wf.form.steps]);

  // Compute per-step validation warnings for the DAG
  const stepWarnings = useMemo(() => {
    const map: Record<string, Array<{ message: string; severity: 'error' | 'warning' }>> = {};
    for (const s of wf.form.steps) {
      const warnings: Array<{ message: string; severity: 'error' | 'warning' }> = [];
      const processor = (s.outputConfig as { processor?: unknown } | null)?.processor;
      const isProcessor = Boolean(processor && typeof processor === 'object');
      if (!isProcessor && !s.templateSlug && !s.templateId)
        warnings.push({ message: 'No agent template assigned', severity: 'error' });
      if (!hasContractProperties(s.outputContract))
        warnings.push({ message: 'No output contract', severity: 'warning' });
      // Steps with order > 1 that have no input contracts may need them
      if (s.order > 1 && !s.inputContracts) warnings.push({ message: 'No input contracts', severity: 'warning' });
      if (warnings.length > 0) map[s.id.toString()] = warnings;
    }
    return map;
  }, [wf.form.steps]);

  // Memoize diagram-relevant step data to prevent re-renders on prompt/config edits
  const diagramSteps = useMemo(
    () =>
      wf.form.steps.map((s) => {
        const cfg = s.outputConfig as { parts?: Record<string, unknown> } | null;
        const outputParts = cfg?.parts
          ? Object.entries(cfg.parts)
              .filter(([, v]) => v === true || (typeof v === 'object' && v !== null))
              .map(([name]) => name)
          : [];

        const stepKey = s.id.toString();
        const hasContract = hasContractProperties(s.outputContract);

        return {
          id: stepKey,
          order: s.order,
          name: s.name || '',
          templateSlug: s.templateSlug,
          executionMode: s.executionMode,
          parallelGroup: s.parallelGroup,
          optional: s.optional,
          diagramPosition: s.diagramPosition,
          label: stepLabels.get(stepKey) || String(s.order),
          debugStatus: (debugStatuses[stepKey] ?? (stepResults[stepKey] !== undefined ? 'has_output' : 'none')) as
            | 'none'
            | 'has_output'
            | 'drifted',
          warnings: stepWarnings[stepKey],
          hasContract,
          approved: s.approvalStatus === 'approved',
          outputParts: outputParts.length > 0 ? outputParts : undefined,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      wf.form.steps
        .map((s) => {
          const cfg = s.outputConfig as { parts?: Record<string, unknown> } | null;
          return `${s.id}:${s.order}:${s.name}:${s.templateSlug}:${s.executionMode}:${s.parallelGroup}:${s.optional}:${s.diagramPosition?.x}:${s.diagramPosition?.y}:${s.contractNegotiationStatus}:${s.approvalStatus}:${Object.keys(
            cfg?.parts ?? {},
          )
            .filter((k) => cfg?.parts?.[k])
            .join(',')}`;
        })
        .join('|'),
      stepLabels,
      debugStatuses,
      stepWarnings,
    ],
  );

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const isCollapsed = prev.has(section);
      if (isCollapsed) {
        const next = new Set(prev);
        next.delete(section);
        return next;
      } else {
        const others = ['dag', 'steps', 'metadata'].filter((s) => s !== section);
        const allOthersCollapsed = others.every((s) => prev.has(s));
        if (allOthersCollapsed) return new Set([section]);
        const next = new Set(prev);
        next.add(section);
        return next;
      }
    });
  };

  const addStep = () => {
    const step = wf.addStep();
    setSelectedStep(step.id.toString());
    setActiveTab('config');
  };

  const removeStep = (stepId: string) => {
    wf.removeStep(stepId);
    if (selectedStep === stepId) {
      setSelectedStep(null);
      setPromptPreview(null);
    }
  };

  const updateStep = (stepId: string, updates: Partial<WorkflowBuilderStep>) => {
    wf.updateStep(stepId, updates);
    if ('templateSlug' in updates || 'templateId' in updates) setPromptPreview(null);
  };

  const [saveWarnings, setSaveWarnings] = useState<Array<SaveWarning>>([]);

  const sendNegotiateMessage = () => {
    const stepDetails = wf.form.steps.map((s) => ({
      stepRef: s.name || s.id.toString(),
      name: s.name,
      templateSlug: s.templateSlug,
      order: s.order,
      outputContract: s.outputContract,
      inputContracts: s.inputContracts,
      contractNegotiationStatus: s.contractNegotiationStatus,
      processor: (s.outputConfig as { processor?: unknown } | null)?.processor ?? null,
    }));
    setAssistantMessage(
      [
        'Check this workflow data flow and propose user-reviewable fixes.',
        'Use step names/titles in your explanation. Use internal step refs only inside structured updates when needed.',
        'Return one workflow-update block with any contractUpdates and processorProposals.',
        'Use processorProposals for clear deterministic field_map, merge, filter, aggregate, normalize, or dispatch transforms only when the source-to-target operation is explicit.',
        'For semantic transforms, use an llm_transform proposal with mode "llm", explain why deterministic mapping is insufficient, and include the data exposure/contract risk for explicit user acceptance as a new transform step.',
        '',
        JSON.stringify({ action: 'negotiate-contracts', steps: stepDetails }, null, 2),
      ].join('\n'),
    );
  };

  const save = async () => {
    const warnings = wf.validate();
    if (warnings.length > 0) {
      setSaveWarnings(warnings);
      return;
    }
    // Version-bump confirmation.
    //
    // These were two `window.confirm()` calls (QG-CLI-PAGES-011): a native dialog blocks the
    // event loop, so the browser-automation suite cannot drive past it, and it cannot be styled.
    // They are routed through the SAME gate the validation warnings already use — the warnings
    // modal, whose "Save anyway" button calls `confirmSaveWithWarnings`, which re-enters the save
    // past this check. So "user confirmed" has one mechanism on this page instead of two.
    if (!isNew && id) {
      const versionWarning = (message: string): SaveWarning => ({
        stepId: '__workflow__',
        stepName: 'This workflow',
        message,
      });
      try {
        const history = await workflowApi.runs(id);
        const runCount = Array.isArray(history?.data) ? history.data.length : 0;
        if (runCount > 0) {
          setSaveWarnings([
            versionWarning(
              `This workflow has ${runCount} existing run${runCount === 1 ? '' : 's'}. Saving will create workflow version ${(wf.form.version ?? 1) + 1} for future runs; existing run evidence remains unchanged.`,
            ),
          ]);
          return;
        }
      } catch {
        setSaveWarnings([
          versionWarning(
            'Could not check existing workflow runs. Saving may affect future runs of an already-used workflow.',
          ),
        ]);
        return;
      }
    }

    const formEdges = wf.form.edges;
    if (!isNew && id && formEdges.length > 0) {
      // Edge-aware save: full data first (updateWorkflow), then structural graph (saveGraph)
      const newVersion = await wf.saveNoNavigate();
      if (newVersion === null) return; // error surfaced inside saveNoNavigate
      try {
        await graphSave.saveGraph(wf.form.steps, formEdges, newVersion);
        navigate('/agents/workflows');
      } catch {
        // error surfaced in graphSave.saveGraphError; don't navigate
      }
    } else {
      await wf.save(navigate);
    }
  };

  const confirmSaveWithWarnings = async () => {
    setSaveWarnings([]);
    await wf.save(navigate);
  };

  const trigger = async () => {
    if (isNew || !id) return;
    try {
      await workflowApi.trigger(id);
      notify('Workflow triggered');
    } catch (err) {
      notify('Trigger failed: ' + (err as Error).message, 'danger');
    }
  };

  const loadPromptPreview = async () => {
    if (!sel?.templateSlug) {
      setPromptPreview(null);
      return;
    }
    setPromptLoading(true);
    try {
      // Send the step definition fields the preview endpoint accepts — server assembles prompts
      // using the same pipeline as real workflow execution (role → model adapter → output
      // channels → contracts). outputContract/inputContracts are free-form jsonb on the step row
      // (typed `unknown` on WorkflowBuilderStep); this is the one place the persisted value crosses into
      // the endpoint's typed request contract, so it's cast to the real generated contract type
      // rather than left `unknown`. promptMode is narrowed because WorkflowBuilderStep's UI-only type
      // still carries legacy 'system'/'user'/'both' values the endpoint's Zod schema rejects.
      const data = await agentApi.previewPrompt({
        templateSlug: sel.templateSlug,
        templateId: sel.templateId,
        name: sel.name,
        description: sel.description,
        systemPromptOverride: sel.systemPromptOverride,
        additionalInstructions: sel.additionalInstructions,
        userPromptTemplate: sel.userPromptTemplate,
        userPromptFreetext: sel.userPromptFreetext,
        promptMode: sel.promptMode === 'template' || sel.promptMode === 'freetext' ? sel.promptMode : undefined,
        outputConfig: sel.outputConfig,
        outputContract: sel.outputContract as WorkflowContract,
        inputContracts: sel.inputContracts as InputContractMap,
      });
      setPromptPreview(data);
    } catch (err) {
      console.error('Failed to load prompt preview:', err);
      setPromptPreview(null);
    } finally {
      setPromptLoading(false);
    }
  };

  const handlePositionsChange = useCallback(
    (positions: Record<string, { x: number; y: number }>) => {
      wf.setSteps(
        wf.form.steps.map((s) =>
          positions[s.id.toString()] ? { ...s, diagramPosition: positions[s.id.toString()] } : s,
        ),
      );
    },
    [wf],
  );

  // Page-level drag-and-drop for external AI file import (extracted to useWorkflowFileDrop)
  const { dragOver, handleDragOver, handleDragEnter, handleDragLeave, handleDrop } = useWorkflowFileDrop(
    useCallback(
      (content: string, fileName: string) =>
        setAssistantMessage(buildWorkflowAssistantImportMessage(content, fileName)),
      [],
    ),
  );

  // ── Graph save mutation ──────────────────────────────────────────────────
  const graphSave = useGraphSave(!isNew && id ? id : undefined);

  // ── Canvas edge management ───────────────────────────────────────────────
  const addEdge = useCallback(
    (sourceStepId: string, targetStepId: string) => {
      const src = Number(sourceStepId);
      const tgt = Number(targetStepId);
      if (src === tgt) return;
      if (wf.form.edges.find((e) => e.sourceStepId === src && e.targetStepId === tgt)) return;
      wf.addEdge({ sourceStepId: src, targetStepId: tgt, kind: 'data' });
    },
    [wf],
  );

  const removeEdge = useCallback(
    (sourceStepId: string, targetStepId: string) => {
      wf.removeEdge(Number(sourceStepId), Number(targetStepId));
    },
    [wf],
  );

  // Called when a palette node is dropped onto the canvas
  const handleDropStep = useCallback(
    (kind: string, position: { x: number; y: number }) => {
      const mode = kind === 'fan-out' ? 'parallel' : 'sequential';
      const nameMap: Record<string, string> = {
        agent: 'Agent Step',
        'sub-workflow': 'Sub-workflow',
        processor: 'Processor',
        'fan-out': 'Fan-out Step',
      };
      const step = wf.addStep();
      wf.updateStep(step.id.toString(), {
        diagramPosition: position,
        executionMode: mode as WorkflowBuilderStep['executionMode'],
        name: nameMap[kind] ?? kind,
      });
      setSelectedStep(step.id.toString());
      setActiveTab('config');
    },
    [wf],
  );

  const dagCollapsed = collapsedSections.has('dag');
  const stepsCollapsed = collapsedSections.has('steps');
  const dagExpanded = !dagCollapsed && stepsCollapsed && collapsedSections.has('metadata');
  const stepsExpanded = !stepsCollapsed && dagCollapsed && collapsedSections.has('metadata');

  return {
    // Route
    id,
    isNew,
    workflowId,
    // Form
    wf,
    changes,
    // Step selection
    selectedStep,
    setSelectedStep,
    sel,
    activeTab,
    setActiveTab,
    // Layout
    layoutMode,
    setLayoutMode,
    // Focus/sections
    focusMode,
    setFocusMode,
    collapsedSections,
    setCollapsedSections,
    dagCollapsed,
    stepsCollapsed,
    dagExpanded,
    stepsExpanded,
    toggleSection,
    // Steps
    addStep,
    removeStep,
    updateStep,
    stepLabels,
    stepResults,
    setStepResults,
    diagramSteps,
    // Prompt preview
    promptPreview,
    promptLoading,
    instructionMode,
    setInstructionMode,
    // Resizer
    // Roles
    roles,
    // Multi-stream
    multiStream,
    // Positions
    handlePositionsChange,
    // Drag-and-drop
    dragOver,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    // External document / assistant
    assistantMessage,
    setAssistantMessage,
    // Modals
    pendingChangesOpen,
    setPendingChangesOpen,
    exportOpen,
    setExportOpen,
    // Save
    save,
    confirmSaveWithWarnings,
    saveWarnings,
    setSaveWarnings,
    trigger,
    sendNegotiateMessage,
    // Debug
    debugStatuses,
    agentApi,
    // Validation
    stepWarnings,
    // Canvas editing
    addEdge,
    removeEdge,
    handleDropStep,
    saveGraphError: graphSave.saveGraphError,
    clearSaveGraphError: graphSave.clearSaveGraphError,
    isSavingGraph: graphSave.isSavingGraph,
  };
}
